import { defaultLogRetentionConfig } from "@/logging/config";
import { sanitise } from "@/logging/logUtil"
import { loggingDb } from "@/logging/logDb";
import type { LogCategory, LogEntry, LogLevel, LogWriteOptions } from '@/logging/types';
import type { LogRetentionConfig } from '@/logging/config';

/**
 * Numeric priority for each log level — higher value means more severe.
 * Only levels >= the active minimum level are persisted.
 * @type {Object.<string, number>}
 */
const LOG_LEVEL_PRIORITY = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
};

const LEVEL_INFO = LOG_LEVEL_PRIORITY.INFO;
const LEVEL_DEBUG = LOG_LEVEL_PRIORITY.DEBUG;

const RETENTION_THROTTLE_COUNT = 50;

export class LogStore {
    config: LogRetentionConfig;
    _diagnosticMode: boolean;
    _writesSinceRetention: number;

    constructor(config: LogRetentionConfig = defaultLogRetentionConfig) {
        this.config = config;
        this._diagnosticMode = config.diagnosticMode ?? false;
        this._writesSinceRetention = 0;
    }

    /**
     * Returns whether diagnostic mode is currently enabled.
     * @returns {boolean}
     */
    get diagnosticMode() {
        return this._diagnosticMode;
    }

    /**
     * Enables or disables diagnostic mode.
     * @param {boolean} enabled
     */
    setDiagnosticMode(enabled: boolean) {
        this._diagnosticMode = Boolean(enabled);
    }

    /**
     * Returns the minimum log-level priority that will be persisted
     * based on current diagnostic mode.
     * @returns {number}
     */
    get _minLevel() {
        return this._diagnosticMode ? LEVEL_DEBUG : LEVEL_INFO;
    }

    async write(
        level: LogLevel,
        category: LogCategory,
        event: string,
        context?: unknown,
        message?: string,
        options: LogWriteOptions = {},
    ): Promise<void> {
        if ((LOG_LEVEL_PRIORITY[level] ?? LEVEL_DEBUG) < this._minLevel) {
            return;
        }

        const sanitisedContext = sanitise(context) || {};
        const logEntry: LogEntry = {
            timestamp: new Date().toISOString(),
            level,
            category,
            event,
            context: sanitisedContext,
            message,
            sessionId: options.sessionId,
            logSessionId: options.logSessionId,
            diagnosticMode: this._diagnosticMode,
        }

        try {
            await loggingDb.logs.add(logEntry);
            this._writesSinceRetention++;
            if (this._writesSinceRetention >= RETENTION_THROTTLE_COUNT) {
                this._writesSinceRetention = 0;
                await this.enforceRetention();
            }
        } catch (e: unknown) {
            console.warn(`Failed to persist log entry to IndexedDB ${e}`);
        }
    }

    /**
     * Returns all persisted log entries.
     * @returns {Promise<Array>}
     */
    async getAllLogs(): Promise<LogEntry[]> {
        try {
            return await loggingDb.logs.toArray();
        } catch (e: unknown) {
            console.warn(`Failed to read log entries from IndexedDB ${e}`);
            return [];
        }
    }

    async clearLogs(): Promise<void> {
        try {
            await loggingDb.logs.clear();
        } catch (e: unknown) {
            console.warn(`Failed to clear logs entries from IndexedDB ${e}`);
        }
    }

    /**
     * Enforces retention by deleting expired entries and
     * any excess entries over the max configured entries.
     * @returns {Promise<void>}
     */
    async enforceRetention(): Promise<void> {
        try {
            await this.purgeExpired();
            await this.purgeExcess();
        } catch (e: unknown) {
            console.warn(`Failed to enforce log retention ${e}`);
        }
    }


    /**
     * Deletes expired entries based on their timestamp and the max age in config.
     * @returns {Promise<void>}
     */
    async purgeExpired(): Promise<void> {
        const expireIso =  new Date(Date.now() - this.config.maxAgeMs).toISOString();

        await loggingDb.logs.where("timestamp").below(expireIso).delete();
    }

    /**
     * Deletes any entries where there are more than configured max entries.
     * @returns {Promise<void>}
     */
    async purgeExcess(): Promise<void> {
        const count = await loggingDb.logs.count();
        const excess = count - this.config.maxEntries;

        if (excess <= 0) {
            return;
        }

        const oldestIds = await loggingDb.logs
            .orderBy("timestamp")
            .limit(excess)
            .primaryKeys();

        if (oldestIds.length > 0) {
            await loggingDb.logs.bulkDelete(oldestIds);
        }
    }
}

export const logStore = new LogStore();
